import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { setLanguage, DEFAULT_LANGUAGE } from "@/i18n";

const AuthContext = createContext(null);

// The account's saved language is the source of truth once signed in, so each
// person's chosen language follows them to any device without re-selecting.
// (localStorage is only a pre-login guess for the sign-in screen.)
function applyUserLanguage(u) {
  if (!u) return;
  setLanguage(u.language || DEFAULT_LANGUAGE);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, object=user
  const [motel, setMotel] = useState(null);

  useEffect(() => {
    // Session lives in an httpOnly cookie; ask the server who we are.
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data.user || false);
        setMotel(data.motel);
        applyUserLanguage(data.user);
      })
      .catch(() => setUser(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password, propertyId) => {
    await api.post("/auth/login", { email, password, property_id: propertyId || undefined });
    const me = await api.get("/auth/me");
    setUser(me.data.user);
    setMotel(me.data.motel);
    applyUserLanguage(me.data.user);
    return me.data.user;
  };

  const signup = async (payload) => {
    await api.post("/auth/signup", payload);
    const me = await api.get("/auth/me");
    setUser(me.data.user);
    setMotel(me.data.motel);
    return me.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* clearing anyway */ }
    setUser(false);
    setMotel(null);
  };

  const impersonate = async (motelId, ghost = false) => {
    await api.post(`/admin/impersonate/${motelId}?ghost=${ghost ? "true" : "false"}`);
    const me = await api.get("/auth/me");
    setUser(me.data.user);
    setMotel(me.data.motel);
    return me.data.user;
  };

  const returnToAdmin = async () => {
    await api.post("/admin/impersonate-stop");
    const me = await api.get("/auth/me");
    setUser(me.data.user);
    setMotel(me.data.motel);
    return me.data.user;
  };

  const acceptTerms = async () => {
    await api.post("/terms/accept");
    const me = await api.get("/auth/me");
    setUser(me.data.user);
    setMotel(me.data.motel);
    return me.data.user;
  };

  const refreshMe = async () => {
    const me = await api.get("/auth/me");
    setUser(me.data.user || false);
    setMotel(me.data.motel);
    return me.data.user;
  };

  const startDemo = async () => {
    await api.post("/demo/start");
    return refreshMe();
  };

  const switchDemo = async (role, cleanerId) => {
    await api.post("/demo/switch", { role, cleaner_id: cleanerId });
    return refreshMe();
  };

  const resetDemo = async () => {
    await api.post("/demo/reset");
    return refreshMe();
  };

  const endDemo = async () => {
    try { await api.post("/demo/end"); } catch (e) { /* clearing anyway */ }
    setUser(false);
    setMotel(null);
  };

  return (
    <AuthContext.Provider value={{ user, motel, login, signup, logout, impersonate, returnToAdmin, acceptTerms, startDemo, switchDemo, resetDemo, endDemo }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
